import {memo, useCallback, useState} from "react";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import {useGetBigWinners} from "@/hooks/gamesList/useGetBigWinners";
import useRequireAuth from "@/hooks/useRequireAuth";
import useContactStore from "@/store/Contacts/useContact.store";
import {formatAmountSuffix} from "@/util/balance.util";
import {formatCurrency} from "@/util/formatCurrency";
import styles from "./LiveWinners.module.css";

const FALLBACK_ICON = "https://cdn.randrush.com/assets/placeholder-game.webp";

const AvatarImg = memo(({src, alt}) => {
  const [failed, setFailed] = useState(false);
  const imgSrc = failed ? FALLBACK_ICON : src || FALLBACK_ICON;

  return (
    <img
      className={styles.avatar}
      src={imgSrc}
      alt={alt}
      draggable={false}
      onError={() => {
        if (!failed) setFailed(true);
      }}
    />
  );
});

AvatarImg.displayName = "AvatarImg";

const WinnerRow = memo(({winner, onClick, onProfileClick, isContact}) => (
  <div
    className={styles.row}
    onClick={() => winner.game_id && winner.provider && onClick(winner.game_id, winner.provider)}
    onKeyDown={e => e.key === "Enter" && winner.game_id && winner.provider && onClick(winner.game_id, winner.provider)}
    role={winner.game_id ? "button" : undefined}
    tabIndex={winner.game_id ? 0 : undefined}
  >
    <AvatarImg
      src={winner.icon}
      alt={winner.game_name}
    />
    <div className={styles.info}>
      <span className={styles.userRow}>
        <button
          type="button"
          className={styles.userLink}
          onClick={e => {
            e.stopPropagation();
            onProfileClick(winner.user_alias);
          }}
        >
          @{winner.user_alias}
        </button>
        {isContact && (
          <span
            className={styles.contactBadge}
            title="Contacto"
          >
            ✓
          </span>
        )}
      </span>
      <span className={styles.gameName}>{winner.game_name}</span>
    </div>
    <span className={styles.amount}>
      +{formatAmountSuffix(winner.amount_float)} {formatCurrency(winner.currency)}
    </span>
  </div>
));

WinnerRow.displayName = "WinnerRow";

const LiveWinners = () => {
  const {t} = useTranslation();
  const {winners, isPending} = useGetBigWinners();
  const navigate = useNavigate();
  const requireAuth = useRequireAuth();
  const contacts = useContactStore(state => (Array.isArray(state.contacts) ? state.contacts : []));

  const contactAliases = new Set(contacts.map(c => c.alias).filter(Boolean));

  const handleGameClick = useCallback(
    (gameId, provider) => {
      requireAuth(() => navigate(`/games/${provider}/${gameId}`));
    },
    [navigate, requireAuth]
  );

  const handleProfileClick = useCallback(
    alias => {
      requireAuth(() => navigate(`/u/${alias}`));
    },
    [navigate, requireAuth]
  );

  if (isPending || winners.length === 0) return null;

  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <span
          className={styles.headerIcon}
          role="img"
          aria-label="fire"
        >
          🔥
        </span>
        <h3 className={styles.headerTitle}>{t("latestWinners")}</h3>
        <span className={styles.liveDot} />
      </div>
      <div className={styles.listWrapper}>
        <div className={styles.listTrack}>
          {winners.map((w, i) => (
            <WinnerRow
              // biome-ignore lint/suspicious/noArrayIndexKey: duplicated list for infinite scroll
              key={`a-${i}`}
              winner={w}
              onClick={handleGameClick}
              onProfileClick={handleProfileClick}
              isContact={contactAliases.has(w.user_alias)}
            />
          ))}
          {winners.map((w, i) => (
            <WinnerRow
              // biome-ignore lint/suspicious/noArrayIndexKey: duplicated list for infinite scroll
              key={`b-${i}`}
              winner={w}
              onClick={handleGameClick}
              onProfileClick={handleProfileClick}
              isContact={contactAliases.has(w.user_alias)}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default memo(LiveWinners);
